import type { PortfolioWithInstructions, RegistryDevice } from "@/types";
import { addDurationToDate } from "@/utils/durationUtils";
import { createDeviceOptions } from "./ppaDeviceUtils";
import {
  type AllocationMode,
  isHundredPercent,
  isPpaContractExpired,
  normalizePercentage,
} from "./ppaPortfolioUtils";

/**
 * Device validation utilities for PPA components
 */

export interface PeriodRange {
  startDate: Date;
  endDate: Date;
  portfolioName: string;
}

export interface DeviceUsage {
  portfolios: PortfolioWithInstructions[];
  hasFixedLimit: boolean;
  hasAllMode: boolean;
  isUsedByCurrentPortfolio: boolean;
  periods: PeriodRange[];
}

export interface DeviceValidationResult {
  totalPercentage: number;
  hasModeMixingError: boolean;
  errorMessage: string | null;
}

/**
 * Extract period ranges from portfolio limits
 */
function extractPeriodRanges(
  portfolio: PortfolioWithInstructions
): PeriodRange[] {
  const limits = portfolio.portfolio_instructions?.limits || [];
  return limits
    .filter(
      (limit: any) => limit?.period?.start_date && limit?.period?.duration
    )
    .map((limit: any) => {
      const startDate = new Date(limit.period.start_date);
      const endDate = addDurationToDate(startDate, limit.period.duration);
      return {
        startDate,
        endDate,
        portfolioName: portfolio.name,
      };
    });
}

/**
 * Check if two period ranges overlap
 */
function periodsOverlap(period1: PeriodRange, period2: PeriodRange): boolean {
  return (
    period1.startDate < period2.endDate && period2.startDate < period1.endDate
  );
}

/**
 * Check if any periods in the list overlap with given periods
 */
export function hasOverlappingPeriods(
  existingPeriods: PeriodRange[],
  newPeriods: PeriodRange[]
): boolean {
  for (const existing of existingPeriods) {
    for (const newPeriod of newPeriods) {
      if (periodsOverlap(existing, newPeriod)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Analyze device usage across all PPA portfolios
 */
export function analyzeDeviceUsage(
  existingPortfolios: PortfolioWithInstructions[] = [],
  currentPortfolioId?: number,
  currentDeviceIds: string[] = [],
  referenceDate: Date = new Date()
): Record<string, DeviceUsage> {
  const deviceUsage: Record<string, DeviceUsage> = {};

  // Analyze existing portfolios (excluding current one being edited)
  existingPortfolios.forEach((portfolio) => {
    // Skip the current portfolio being edited (compare by ID) and PPAs whose contract has ended:
    // they no longer allocate, so their devices are free for a successor PPA.
    if (
      portfolio.id !== currentPortfolioId &&
      !isPpaContractExpired(portfolio.action_instructions, referenceDate)
    ) {
      const deviceIds =
        portfolio.portfolio_instructions?.registry_device_ids || [];
      const hasLimits =
        (portfolio.portfolio_instructions?.limits || []).length > 0;
      const percentageValue = normalizePercentage(
        portfolio.portfolio_instructions?.allocate_percentage ?? null
      );
      const isAllMode = !hasLimits && isHundredPercent(percentageValue);

      // Extract period ranges for period-aware validation
      const periodRanges = hasLimits ? extractPeriodRanges(portfolio) : [];

      deviceIds.forEach((deviceId: any) => {
        const deviceIdStr = deviceId.toString();
        if (!deviceUsage[deviceIdStr]) {
          deviceUsage[deviceIdStr] = {
            portfolios: [],
            hasFixedLimit: false,
            hasAllMode: false,
            isUsedByCurrentPortfolio: false,
            periods: [],
          };
        }

        deviceUsage[deviceIdStr].portfolios.push(portfolio);
        if (hasLimits) {
          deviceUsage[deviceIdStr].hasFixedLimit = true;
          deviceUsage[deviceIdStr].periods.push(...periodRanges);
        }
        if (isAllMode) {
          deviceUsage[deviceIdStr].hasAllMode = true;
        }
      });
    }
  });

  // Mark devices used by current portfolio
  currentDeviceIds.forEach((deviceId: any) => {
    const deviceIdStr = deviceId.toString();
    if (!deviceUsage[deviceIdStr]) {
      deviceUsage[deviceIdStr] = {
        portfolios: [],
        hasFixedLimit: false,
        hasAllMode: false,
        isUsedByCurrentPortfolio: false,
        periods: [],
      };
    }
    deviceUsage[deviceIdStr].isUsedByCurrentPortfolio = true;
  });

  return deviceUsage;
}

/**
 * Validate device allocation conflicts
 *
 * @param currentPeriods - Optional periods for the current portfolio (for period-aware validation)
 */
export function validateDeviceAllocation(
  deviceId: string,
  currentMode: AllocationMode,
  currentPercentage: number,
  deviceUsage: DeviceUsage,
  t: (key: string, params?: any) => string,
  getDeviceName: (deviceId: string) => string,
  currentPeriods: PeriodRange[] = []
): DeviceValidationResult {
  let totalPercentage = 0;
  let hasModeMixingError = false;
  let errorMessage: string | null = null;

  if (deviceUsage.portfolios.length > 0) {
    // Check for mode mixing conflicts
    if (currentMode === "percentage") {
      // Percentage mode: can't mix with fixed-limit or all-mode PPAs
      if (deviceUsage.hasFixedLimit) {
        hasModeMixingError = true;
        errorMessage = t(
          "portfolio_instructions.device_used_in_fixed_limit_ppa",
          {
            deviceId: getDeviceName(deviceId),
          }
        );
      } else if (deviceUsage.hasAllMode) {
        hasModeMixingError = true;
        errorMessage = t(
          "portfolio_instructions.device_assigned_to_100_percent_ppa",
          { deviceId: getDeviceName(deviceId) }
        );
      }
    } else if (currentMode === "fixed-amounts") {
      // Fixed-amounts mode: can only mix with other fixed-amounts PPAs (with non-overlapping periods)
      if (deviceUsage.hasAllMode) {
        // Can't mix with 100% allocation PPAs
        hasModeMixingError = true;
        errorMessage = t(
          "portfolio_instructions.device_cannot_mix_with_exclusive_mode",
          {
            deviceId: getDeviceName(deviceId),
            currentMode: t("portfolio_instructions.fixed_amounts_mode"),
          }
        );
      } else if (deviceUsage.hasFixedLimit) {
        // Other fixed-limit PPAs exist - check if periods overlap
        if (
          currentPeriods.length > 0 &&
          hasOverlappingPeriods(deviceUsage.periods, currentPeriods)
        ) {
          hasModeMixingError = true;
          errorMessage = t(
            "portfolio_instructions.device_cannot_mix_with_exclusive_mode",
            {
              deviceId: getDeviceName(deviceId),
              currentMode: t("portfolio_instructions.fixed_amounts_mode"),
            }
          );
        }
      } else if (deviceUsage.portfolios.length > 0) {
        // Device is used by percentage-only PPAs - can't mix modes
        hasModeMixingError = true;
        errorMessage = t(
          "portfolio_instructions.device_cannot_mix_with_exclusive_mode",
          {
            deviceId: getDeviceName(deviceId),
            currentMode: t("portfolio_instructions.fixed_amounts_mode"),
          }
        );
      }
    } else if (currentMode === "all") {
      // All mode: can't mix with any other PPA mode
      hasModeMixingError = true;
      errorMessage = t(
        "portfolio_instructions.device_cannot_mix_with_exclusive_mode",
        {
          deviceId: getDeviceName(deviceId),
          currentMode: t("portfolio_instructions.all_mode"),
        }
      );
    }

    // If no mode mixing error, check percentage calculations (only for percentage mode)
    if (!hasModeMixingError && currentMode === "percentage") {
      // Calculate total percentage across all percentage-only PPAs for this device
      deviceUsage.portfolios.forEach((portfolio) => {
        const portfolioPercentage = normalizePercentage(
          portfolio.portfolio_instructions?.allocate_percentage ?? null
        );
        const portfolioHasLimits =
          (portfolio.portfolio_instructions?.limits || []).length > 0;

        // Only count percentage-only PPAs
        if (
          !portfolioHasLimits &&
          portfolioPercentage !== null &&
          portfolioPercentage > 0
        ) {
          totalPercentage =
            Math.round((totalPercentage + portfolioPercentage) * 100) / 100;
        }
      });

      // Add current portfolio's percentage
      totalPercentage =
        Math.round((totalPercentage + currentPercentage) * 100) / 100;

      // Check if total exceeds 100%
      if (totalPercentage > 100) {
        errorMessage = t("portfolio_instructions.device_exceeds_100_percent", {
          deviceId: getDeviceName(deviceId),
          totalPercentage,
        });
      }
    }
  } else if (currentMode === "percentage") {
    // Device not used by others, just add current percentage for percentage mode
    totalPercentage = Math.round(currentPercentage * 100) / 100;
  }

  return {
    totalPercentage,
    hasModeMixingError,
    errorMessage,
  };
}

/**
 * Validate all selected devices for allocation conflicts
 *
 * @param currentPeriods - Optional periods for the current portfolio (for period-aware validation)
 */
export function validateAllDevices(
  currentDeviceIds: string[],
  currentMode: AllocationMode,
  currentPercentage: number | null | undefined,
  deviceUsageAnalysis: Record<string, DeviceUsage>,
  t: (key: string, params?: any) => string,
  getDeviceName: (deviceId: string) => string,
  currentPeriods: PeriodRange[] = []
): Record<string, DeviceValidationResult> {
  const validation: Record<string, DeviceValidationResult> = {};
  const normalizedCurrentPercentage =
    normalizePercentage(currentPercentage) ?? 0;

  currentDeviceIds.forEach((deviceId) => {
    const deviceIdStr = deviceId.toString();
    const usage = deviceUsageAnalysis[deviceIdStr];

    validation[deviceIdStr] = validateDeviceAllocation(
      deviceId,
      currentMode,
      normalizedCurrentPercentage,
      usage || {
        portfolios: [],
        hasFixedLimit: false,
        hasAllMode: false,
        isUsedByCurrentPortfolio: false,
        periods: [],
      },
      t,
      getDeviceName,
      currentPeriods
    );
  });

  return validation;
}

/**
 * Filter available devices based on allocation mode constraints
 *
 * @param currentPeriods - Optional periods for the current portfolio (for period-aware validation)
 */
export function getAvailableDeviceOptions(
  registryDevices: RegistryDevice[],
  allocationMode: AllocationMode,
  deviceUsageAnalysis: Record<string, DeviceUsage>,
  currentPeriods: PeriodRange[] = []
) {
  // Safety check: ensure registryDevices exists and is an array
  if (!registryDevices || !Array.isArray(registryDevices)) {
    return [];
  }

  // Determine current portfolio mode from selected allocation mode
  const isLimitsMode = allocationMode === "fixed-amounts";
  const isAllMode = allocationMode === "all";
  const isPercentageMode = allocationMode === "percentage";

  return registryDevices
    .filter((device) => {
      // Ensure device has required properties
      if (!device || typeof device.id === "undefined") {
        return false;
      }

      const deviceIdStr = device.id.toString();
      const usage = deviceUsageAnalysis[deviceIdStr];

      // If device not used by any portfolio, it's available for any mode
      if (
        !usage ||
        (usage.portfolios.length === 0 && !usage.isUsedByCurrentPortfolio)
      ) {
        return true;
      }

      // A currently-selected device must always stay in the options list;
      // otherwise the Listbox write-back drops it from the selection.
      // Conflicts are surfaced by validateDeviceAllocation error messages.
      if (usage.isUsedByCurrentPortfolio) {
        return true;
      }

      // Apply mode-specific rules
      if (isPercentageMode) {
        // Percentage mode: allow devices used by percentage-only PPAs (but not 100% or fixed-limit)
        return !usage.hasFixedLimit && !usage.hasAllMode;
      } else if (isLimitsMode) {
        // Fixed-amounts mode: allow if periods don't overlap
        if (usage.hasFixedLimit && currentPeriods.length > 0) {
          // Only block if periods overlap
          return !hasOverlappingPeriods(usage.periods, currentPeriods);
        }
        // Can't mix with 100% allocation PPAs
        return !usage.hasAllMode && usage.portfolios.length === 0;
      } else if (isAllMode) {
        // All mode: device must be exclusive (not used by any other portfolio)
        return usage.portfolios.length === 0;
      }

      // Default: allow if no conflicts
      return true;
    })
    .map((device) => createDeviceOptions([device])[0])
    .filter(Boolean);
}

/**
 * Check if there are any device validation errors
 */
export function hasValidationErrors(
  validation: Record<string, DeviceValidationResult>
): boolean {
  return Object.values(validation).some((v) => v.errorMessage !== null);
}

/**
 * Create PeriodRange array from limits for use in validation functions.
 * Call this with the current portfolio's limits to enable period-aware validation.
 */
export function createPeriodsFromLimits(
  limits: any[],
  portfolioName: string = "current"
): PeriodRange[] {
  if (!limits || limits.length === 0) {
    return [];
  }

  return limits.map((limit: any) => {
    const startDate = new Date(limit.period.start_date);
    const endDate = addDurationToDate(startDate, limit.period.duration);
    return {
      startDate,
      endDate,
      portfolioName,
    };
  });
}
